import { z } from "@core/zod";

const AgentMessagePayloadSchema = z.object(
  { text: z.string(), timestamp: z.string(), type: z.literal("agent-message") },
  {
    text: "The text of the agent's last message in the turn.",
    timestamp: "When the event was recorded, as an ISO 8601 timestamp.",
    type: "The event's type, a message from the agent.",
  },
);

const CommandRunPayloadSchema = z.object(
  {
    command: z.string(),
    timestamp: z.string(),
    type: z.literal("command-run"),
  },
  {
    command: "The shell command the agent ran.",
    timestamp: "When the event was recorded, as an ISO 8601 timestamp.",
    type: "The event's type, a shell command run.",
  },
);

const ContextResetPayloadSchema = z.object(
  {
    reason: z.enum(["cleared", "compacted"]),
    timestamp: z.string(),
    type: z.literal("context-reset"),
  },
  {
    reason: "How the context was reset, either cleared or compacted.",
    timestamp: "When the event was recorded, as an ISO 8601 timestamp.",
    type: "The event's type, a reset of the session's context.",
  },
);

const DocRequiredPayloadSchema = z.object(
  { doc: z.string(), timestamp: z.string(), type: z.literal("doc-required") },
  {
    doc: "The path of the document the agent was required to read.",
    timestamp: "When the event was recorded, as an ISO 8601 timestamp.",
    type: "The event's type, a document required of the agent.",
  },
);

const FileEditPayloadSchema = z.object(
  { path: z.string(), timestamp: z.string(), type: z.literal("file-edit") },
  {
    path: "The path of the file edited, relative to the workspace root.",
    timestamp: "When the event was recorded, as an ISO 8601 timestamp.",
    type: "The event's type, an edit or deletion of a file.",
  },
);

const FileReadPayloadSchema = z.object(
  { path: z.string(), timestamp: z.string(), type: z.literal("file-read") },
  {
    path: "The path of the file read, relative to the workspace root.",
    timestamp: "When the event was recorded, as an ISO 8601 timestamp.",
    type: "The event's type, a read of a file.",
  },
);

const FileWritePayloadSchema = z.object(
  { path: z.string(), timestamp: z.string(), type: z.literal("file-write") },
  {
    path: "The path of the file created, relative to the workspace root.",
    timestamp: "When the event was recorded, as an ISO 8601 timestamp.",
    type: "The event's type, a creation of a file.",
  },
);

const PromptPayloadSchema = z.object(
  { text: z.string(), timestamp: z.string(), type: z.literal("prompt") },
  {
    text: "The text of the prompt the user submitted.",
    timestamp: "When the event was recorded, as an ISO 8601 timestamp.",
    type: "The event's type, a prompt from the user.",
  },
);

const ToolCallPayloadSchema = z.object(
  { timestamp: z.string(), tool: z.string(), type: z.literal("tool-call") },
  {
    timestamp: "When the event was recorded, as an ISO 8601 timestamp.",
    tool: "The name of the tool the agent called.",
    type: "The event's type, a call of a tool touching no file or command.",
  },
);

const TurnEndPayloadSchema = z.object(
  { timestamp: z.string(), type: z.literal("turn-end") },
  {
    timestamp: "When the event was recorded, as an ISO 8601 timestamp.",
    type: "The event's type, the end of the agent's turn.",
  },
);

export const AgentSessionEventPayloadSchema = z.discriminatedUnion("type", [
  AgentMessagePayloadSchema,
  CommandRunPayloadSchema,
  ContextResetPayloadSchema,
  DocRequiredPayloadSchema,
  FileEditPayloadSchema,
  FileReadPayloadSchema,
  FileWritePayloadSchema,
  PromptPayloadSchema,
  ToolCallPayloadSchema,
  TurnEndPayloadSchema,
]);
